import { IChatFormatBlock, IChatInlineNode } from './chat-format.pipe';

// Long messages are shortened the way WhatsApp does it: by visible characters (formatting markers don't count), never
// mid-word, after formatting has been applied - so a cut can't leave a marker open or split a link.

export const TRUNCATION_ELLIPSIS = ' …';

export interface IChatTruncatedBlocks {
  blocks: IChatFormatBlock[];
  isTruncated: boolean;
}

const WHITESPACE_PATTERN = /\s/;

// Index to cut `text` at so that at most `budget` characters remain without splitting a word; 0 when not even one word fits.
export function wordBoundaryCut(text: string, budget: number): number {
  if (budget <= 0) {
    return 0;
  }
  for (let index = Math.min(budget, text.length - 1); index > 0; index--) {
    if (WHITESPACE_PATTERN.test(text[index])) {
      return index;
    }
  }
  return 0;
}

// Styled nodes (bold/italic/strikethrough) wrap a single child chain carrying the same value, so each level is cut alike.
function sliceNode(node: IChatInlineNode, length: number): IChatInlineNode {
  return {
    ...node,
    value: node.value.slice(0, length),
    children: node.children?.map((child) => sliceNode(child, length)),
  };
}

function sliceBlock(block: IChatFormatBlock, lines: IChatInlineNode[][]): IChatFormatBlock {
  return { ...block, lines, orderedMarkers: block.orderedMarkers?.slice(0, lines.length) };
}

function appendEllipsis(blocks: IChatFormatBlock[]): IChatFormatBlock[] {
  if (!blocks.length) {
    return [{ type: 'paragraph', lines: [[{ type: 'text', value: TRUNCATION_ELLIPSIS.trim() }]] }];
  }
  // Builds a new last block instead of changing the given one.
  const lastBlock = blocks[blocks.length - 1];
  const lastLine = lastBlock.lines[lastBlock.lines.length - 1];
  const lastNode = lastLine[lastLine.length - 1];
  const nodes =
    lastNode && lastNode.type !== 'link' && lastNode.value !== lastNode.value.trimEnd()
      ? [...lastLine.slice(0, lastLine.length - 1), sliceNode(lastNode, lastNode.value.trimEnd().length)]
      : lastLine;
  const ellipsis: IChatInlineNode = { type: 'text', value: TRUNCATION_ELLIPSIS };
  const lines = [...lastBlock.lines.slice(0, lastBlock.lines.length - 1), [...nodes, ellipsis]];
  return [...blocks.slice(0, blocks.length - 1), { ...lastBlock, lines }];
}

/**
 * Shortens formatted chat blocks to `limit` visible characters. A line break counts as one character, like the newline it
 * comes from. Links are kept whole or dropped, never cut.
 */
export function truncateChatBlocks(blocks: IChatFormatBlock[], limit: number): IChatTruncatedBlocks {
  let remaining = limit;
  let hasContent = false;
  const truncatedBlocks: IChatFormatBlock[] = [];

  for (const block of blocks) {
    const lines: IChatInlineNode[][] = [];

    for (const line of block.lines) {
      if (truncatedBlocks.length || lines.length) {
        remaining--;
      }

      const nodes: IChatInlineNode[] = [];
      for (const node of line) {
        if (node.value.length <= remaining) {
          nodes.push(node);
          remaining -= node.value.length;
          hasContent ||= node.value.trim().length > 0;
          continue;
        }

        // Only a single word longer than the whole limit is cut mid-word, so the message never collapses to nothing.
        const cut = node.type === 'link' ? 0 : wordBoundaryCut(node.value, remaining) || (hasContent ? 0 : Math.max(remaining, 0));
        if (cut > 0) {
          nodes.push(sliceNode(node, cut));
        }
        // A line or block left empty by the cut is dropped, so the ellipsis follows the last visible text.
        if (nodes.length) {
          lines.push(nodes);
        }
        if (lines.length) {
          truncatedBlocks.push(sliceBlock(block, lines));
        }
        return { blocks: appendEllipsis(truncatedBlocks), isTruncated: true };
      }
      lines.push(nodes);
    }

    truncatedBlocks.push(sliceBlock(block, lines));
  }

  return { blocks, isTruncated: false };
}
